import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFieldArray, useForm, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useToast } from '@/contexts/ToastContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/searchable-select';
import { FormError } from '@/components/ui/form-error';
import { PageLoading } from '@/components/ui/loading';
import { DatePicker } from '@/components/ui/date-picker';
import { Camera, Plus, Trash2, Save, Send } from 'lucide-react';
import type { Project, LabourCategory, ApprovedRequirementOption, EligibleWorkerEntry, LabourRate } from '@/lib/types';
import { requiredText, dateFieldRule, rateRule, quantityRule, positiveIntegerRule, positiveNumberRule, timeRangeSuperRefine, REQUIRED_FIELDS_MESSAGE } from '@/lib/validation-rules';
import { focusFirstInvalidField, handleApiSubmitError } from '@/lib/form-validation';
import { cn } from '@/lib/utils';
import { workedMinutes, workerTotal, formatWorkedHours } from '@/lib/actual-labour';
import { normalizeRateBasis, type RateBasis } from '@shared/labour-costing';
import { useStandardWorkDayHours } from '@/hooks/useStandardWorkDayHours';
import { validateTimeRange } from '@/lib/validation';
import { requirementPrimaryLabel, requirementSecondaryLabel, requirementSearchText } from '@/lib/requirement-labels';
import { PhotographSection } from '@/components/photographs/PhotographSection';

interface Block { id: string; project_id: string; name: string; is_active: boolean; created_at: string; updated_at: string; project_name?: string; }
interface Activity { id: string; project_id: string; block_id: string; name: string; default_unit_id?: string | null; is_active: boolean; default_unit_name?: string | null; }

const lumpsumSchema = z.object({
  categoryId: requiredText('Category', 100),
  mandays: positiveNumberRule('Mandays must be positive.'),
  rate: z.coerce.number().min(0, 'Rate must be zero or positive.').optional().default(0),
  rateBasis: z.enum(['DAILY', 'HOURLY']).optional(),
  timeIn: z.string().min(1, 'Time In is required.'),
  timeOut: z.string().min(1, 'Time Out is required.'),
});

interface WorkerCategoryForm {
  workerCategoryId: string;
  count: number;
  rate: number;
  rateBasis?: RateBasis;
  timeIn?: string;
  timeOut?: string;
  workerEntryIds?: string[];
}

interface ActivityForm {
  activityId: string;
  qty: number;
  unitId?: string;
  workerCategories: WorkerCategoryForm[];
}

interface LumpSumForm {
  categoryId: string;
  mandays: number;
  rate: number;
  rateBasis?: RateBasis;
  timeIn: string;
  timeOut: string;
}

type WorkerCategoryOption = {
  id: string;
  name: string;
  skill_level?: string | null;
  skillLevel?: string | null;
};

interface FormData {
  projectId: string;
  date: string;
  blockId: string;
  requirementId: string;
  activities: ActivityForm[];
  additionalLumpsumRequirements?: LumpSumForm[];
}

function emptyWorkerRow(): WorkerCategoryForm {
  return { workerCategoryId: '', count: 0, rate: 0, rateBasis: 'DAILY', timeIn: '', timeOut: '' };
}

function allocateEligibleWorkerEntryIds(
  activities: ActivityForm[],
  entries: EligibleWorkerEntry[],
): string[][][] {
  const byCategory = new Map<string, string[]>();
  for (const entry of entries) {
    const categoryId = entry.worker_category_id;
    const entryId = entry.worker_entry_id || entry.id;
    if (!categoryId || !entryId) continue;
    const ids = byCategory.get(categoryId) ?? [];
    ids.push(entryId);
    byCategory.set(categoryId, ids);
  }

  const offsets = new Map<string, number>();
  return activities.map((activity) => activity.workerCategories.map((worker) => {
    const categoryId = worker.workerCategoryId;
    if (!categoryId) return [];
    const available = byCategory.get(categoryId) ?? [];
    const offset = offsets.get(categoryId) ?? 0;
    const count = Math.max(0, Number(worker.count) || 0);
    const ids = available.slice(offset, offset + count);
    offsets.set(categoryId, offset + count);
    return ids;
  }));
}

export default function ActualStatusFormPage() {
  const standardHours = useStandardWorkDayHours();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const shouldSubmitRef = useRef(false);
  // Photograph files selected in the section below, uploaded as part of the
  // save flow so a new record (which has no id yet) can carry photographs too.
  const pendingPhotosRef = useRef<File[]>([]);
  // True when a create succeeded but a photograph upload afterwards failed, so
  // the error handler can send the user to the edit screen instead of letting
  // them create a duplicate record on retry.
  const createdForPhotoRef = useRef(false);
  const createdIdRef = useRef<string | null>(null);
  // Guards against a double-click on Save Draft / Send for Approval creating a
  // second record (and re-uploading the same photographs).
  const submittingRef = useRef(false);
  // Tracks whether edit-mode hydration has completed so effects don't clear
  // restored values while async master data (blocks, activities) loads.
  const hasHydratedEditRef = useRef(false);
  // Stores saved unit names from the detail response so the read-only Unit field
  // can display them even when the activity master is inactive and not in the
  // loaded activities list.
  const savedUnitNamesRef = useRef<Record<string, string>>({});
  const [editHydrated, setEditHydrated] = useState(false);

  const schema = useMemo(() => z.object({
    projectId: requiredText('Project', 100),
    date: dateFieldRule('Date'),
    blockId: requiredText('Block', 100),
    requirementId: z.string().optional(),
    activities: z.array(z.object({
      activityId: requiredText('Activity', 100),
      qty: quantityRule,
      unitId: z.string().optional(),
      workerCategories: z.array(z.object({
        workerCategoryId: requiredText('Worker category', 100),
        count: positiveNumberRule('Count must be at least 1.'),
        rate: z.coerce.number().min(0, 'Rate must be zero or positive.').optional().default(0),
        rateBasis: z.enum(['DAILY', 'HOURLY']).optional(),
        timeIn: z.string().min(1, 'Time In is required.'),
        timeOut: z.string().min(1, 'Time Out is required.'),
      })).min(1, 'At least one worker category per activity.'),
    })).min(1, 'At least one activity is required.'),
    additionalLumpsumRequirements: z.array(lumpsumSchema).optional(),
  }).superRefine((data, ctx) => {
    for (let ai = 0; ai < data.activities.length; ai++) {
      const act = data.activities[ai];
      for (let wi = 0; wi < act.workerCategories.length; wi++) {
        const w = act.workerCategories[wi];
        if (!w.timeIn || !w.timeOut) continue;
        const result = validateTimeRange(w.timeIn, w.timeOut, {
          startLabel: 'Time In', endLabel: 'Time Out',
        });
        if (!result.valid) {
          if (result.startError) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['activities', ai, 'workerCategories', wi, 'timeIn'], message: result.startError });
          if (result.endError) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['activities', ai, 'workerCategories', wi, 'timeOut'], message: result.endError });
        }
      }
    }
  }), []);

  const { register, handleSubmit, control, watch, setValue, reset, clearErrors, setError, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      activities: [{ activityId: '', qty: 0, unitId: '', workerCategories: [{ workerCategoryId: '', count: 0, rate: 0, rateBasis: 'DAILY', timeIn: '', timeOut: '' }] }],
      additionalLumpsumRequirements: [],
    },
  });

  const prevDependenciesRef = useRef({ projectId: '', blockId: '', date: '' });
  const prevRequirementIdRef = useRef('');

  const { fields: activityFields, append: appendActivity, remove: removeActivity } = useFieldArray({ control, name: 'activities' });

  const watchedProjectId = watch('projectId');
  const watchedBlockId = watch('blockId');
  const watchedDate = watch('date');
  const watchedActivities = watch('activities');
  const watchedRequirementId = watch('requirementId');

  const { data: projects } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => { const r = await api.get<any>('/projects?availability=active'); return (r.data ?? []) as Project[]; },
  });

  const { data: blocks, isLoading: blocksIsLoading } = useQuery({
    queryKey: ['blocks', 'organization', isEdit],
    queryFn: async () => {
      const qs = new URLSearchParams({ scope: 'organization' });
      if (isEdit) qs.set('includeInactive', '1');
      const r = await api.get<any>(`/blocks/all?${qs.toString()}`);
      return (r.data ?? []) as Block[];
    },
  });

  const {
    data: activities,
    isLoading: activitiesIsLoading,
    isError: activitiesError,
    refetch: refetchActivities,
  } = useQuery({
    queryKey: ['activities', 'organization', isEdit],
    queryFn: async () => {
      const qs = new URLSearchParams({ scope: 'organization' });
      if (isEdit) qs.set('includeInactive', '1');
      const r = await api.get<any>(`/activities/all?${qs.toString()}`);
      return (r.data ?? []) as Activity[];
    },
    retry: 0,
  });

  const { data: rates } = useQuery({
    queryKey: ['rates', 'active'],
    queryFn: async () => {
      const res = await api.get<any>('/rates');
      return (res.data ?? []) as LabourRate[];
    },
  });

  const { data: labourCategories } = useQuery({
    queryKey: ['labour-categories-active'],
    queryFn: async () => { const r = await api.get<any>('/labour-categories/all'); return (r.data ?? []) as LabourCategory[]; },
  });

  // Eligible Requirements for the selected project and date. Returned
  // requirements are limited to the signed-in engineer's own finally
  // CH-approved entries (enforced server-side from the session).
  const {
    data: approvedRequirements,
    isLoading: requirementsLoading,
    isError: requirementsError,
    refetch: refetchRequirements,
  } = useQuery({
    queryKey: ['requirements-approved', watchedProjectId, watchedDate],
    queryFn: async () => {
      const qs = new URLSearchParams({ projectId: watchedProjectId });
      if (watchedDate) qs.set('date', watchedDate);
      const r = await api.get<any>(`/requirements/approved?${qs.toString()}`);
      return (r.data ?? []) as ApprovedRequirementOption[];
    },
    enabled: Boolean(watchedProjectId),
    retry: 0,
  });

  const requirementOptions: SearchableSelectOption[] = useMemo(() => {
    return (approvedRequirements ?? [])
      .filter((r) => {
        if (!Array.isArray(r.eligible_worker_entries) || r.eligible_worker_entries.length === 0) return false;
        if (watchedDate && r.work_date) {
          const start = r.work_date;
          const end = r.work_end_date || start;
          if (watchedDate < start || watchedDate > end) return false;
        }
        return true;
      })
      .map((r) => ({
        value: r.id,
        label: requirementPrimaryLabel(r),
        description: requirementSecondaryLabel(r),
        searchText: requirementSearchText(r),
        sublabel: r.requirement_number,
      }));
  }, [approvedRequirements, watchedDate]);

  // Selected requirement record
  const selectedRequirement = useMemo(() => {
    if (!watchedRequirementId || isEdit) return null;
    return (approvedRequirements ?? []).find((r) => r.id === watchedRequirementId) ?? null;
  }, [watchedRequirementId, approvedRequirements, isEdit]);

  // The requirement whose worker entries gate this record in both create and
  // edit mode. Only entries that individually received PM and CH approval are
  // exposed by the API under eligible_worker_entries.
  const linkedRequirement = useMemo(() => {
    if (!watchedRequirementId) return null;
    return (approvedRequirements ?? []).find((r) => r.id === watchedRequirementId) ?? null;
  }, [watchedRequirementId, approvedRequirements]);

  const eligibleWorkerEntries = useMemo(() => {
    if (!linkedRequirement) return null;
    return Array.isArray(linkedRequirement.eligible_worker_entries)
      ? linkedRequirement.eligible_worker_entries
      : null;
  }, [linkedRequirement]);

  const eligibleCategoryCounts = useMemo(() => {
    if (!eligibleWorkerEntries) return null;
    const counts = new Map<string, { name: string; count: number }>();
    for (const entry of eligibleWorkerEntries) {
      const id = entry.worker_category_id;
      if (!id) continue;
      const name = entry.worker_category_name || entry.category_name || 'Labour';
      const current = counts.get(id) ?? { name, count: 0 };
      current.count += 1;
      counts.set(id, current);
    }
    return counts;
  }, [eligibleWorkerEntries]);

  const workerCategoryOptions = useMemo<WorkerCategoryOption[]>(() => {
    if (!eligibleCategoryCounts) return labourCategories ?? [];
    const seen = new Set<string>();
    const options: WorkerCategoryOption[] = [];
    for (const entry of eligibleWorkerEntries ?? []) {
      const id = entry.worker_category_id;
      if (!id || seen.has(id)) continue;
      seen.add(id);
      options.push({
        id,
        name: entry.worker_category_name || entry.category_name || 'Labour',
        skill_level: entry.skill_level,
      });
    }
    return options;
  }, [eligibleCategoryCounts, eligibleWorkerEntries, labourCategories]);

  useEffect(() => {
    if (!isEdit) {
      const prev = prevDependenciesRef.current;
      const projectChanged = prev.projectId && prev.projectId !== watchedProjectId;
      const blockChanged = prev.blockId && prev.blockId !== watchedBlockId;
      const dateChanged = prev.date && prev.date !== watchedDate;
      prevDependenciesRef.current = {
        projectId: watchedProjectId || '',
        blockId: watchedBlockId || '',
        date: watchedDate || '',
      };

      if (projectChanged) {
        setValue('blockId', '');
        setValue('requirementId', '');
        setValue('date', '');
        clearErrors('requirementId');
        clearErrors('blockId');
        clearErrors('date');
        watchedActivities?.forEach((_, actIdx) => {
          setValue(`activities.${actIdx}.activityId`, '');
          setValue(`activities.${actIdx}.unitId`, '');
          setValue(`activities.${actIdx}.workerCategories`, [emptyWorkerRow()]);
          clearErrors(`activities.${actIdx}.activityId`);
          clearErrors(`activities.${actIdx}.unitId`);
          clearErrors(`activities.${actIdx}.workerCategories`);
        });
      } else {
        if (blockChanged) {
          watchedActivities?.forEach((_, actIdx) => {
            setValue(`activities.${actIdx}.activityId`, '');
            setValue(`activities.${actIdx}.unitId`, '');
            setValue(`activities.${actIdx}.workerCategories`, [emptyWorkerRow()]);
            clearErrors(`activities.${actIdx}.activityId`);
            clearErrors(`activities.${actIdx}.unitId`);
            clearErrors(`activities.${actIdx}.workerCategories`);
          });
        }
        if (dateChanged && watchedRequirementId) {
          const req = (approvedRequirements ?? []).find((r) => r.id === watchedRequirementId);
          if (req?.work_date) {
            const start = req.work_date;
            const end = req.work_end_date || start;
            if (watchedDate && (watchedDate < start || watchedDate > end)) {
              setValue('requirementId', '');
              clearErrors('requirementId');
            }
          }
        }
      }
    }
  }, [watchedProjectId, watchedBlockId, watchedDate, watchedRequirementId, selectedRequirement, isEdit, setValue, clearErrors, watchedActivities, approvedRequirements]);

  // If the selected requirement becomes ineligible after a project/block/date change, clear it safely
  useEffect(() => {
    if (!isEdit && watchedRequirementId && approvedRequirements && !requirementsLoading) {
      const isEligible = requirementOptions.some((o) => o.value === watchedRequirementId);
      if (!isEligible) {
        setValue('requirementId', '');
        clearErrors('requirementId');
      }
    }
  }, [isEdit, watchedRequirementId, requirementOptions, approvedRequirements, requirementsLoading, setValue, clearErrors]);

  // Clear worker rows whenever the linked requirement changes (including when
  // the requirement becomes ineligible), so entries from the previous
  // requirement can never remain visible or be submitted.
  useEffect(() => {
    if (isEdit) return;
    const previous = prevRequirementIdRef.current;
    const current = watchedRequirementId || '';
    prevRequirementIdRef.current = current;
    if (!previous || previous === current) return;
    watchedActivities?.forEach((_, actIdx) => {
      setValue(`activities.${actIdx}.workerCategories`, [emptyWorkerRow()]);
      clearErrors(`activities.${actIdx}.workerCategories`);
    });
  }, [watchedRequirementId, isEdit, watchedActivities, setValue, clearErrors]);

  // Auto-populate Date and Block from the selected Requirement (create mode only)
  useEffect(() => {
    if (!isEdit && selectedRequirement) {
      if (selectedRequirement.block_id && watchedBlockId !== selectedRequirement.block_id) {
        setValue('blockId', selectedRequirement.block_id, { shouldValidate: true, shouldDirty: true });
      }
      if (selectedRequirement.work_date) {
        const start = selectedRequirement.work_date;
        const end = selectedRequirement.work_end_date || start;
        if (!watchedDate || watchedDate < start || watchedDate > end) {
          setValue('date', selectedRequirement.work_date, { shouldValidate: true, shouldDirty: true });
        }
      }
    }
  }, [selectedRequirement, isEdit, setValue, watchedBlockId, watchedDate]);

  useEffect(() => {
    if (isEdit && !hasHydratedEditRef.current) return;
    watchedActivities?.forEach((act, actIdx) => {
      if (act?.activityId) {
        const actObj = activities?.find((a: Activity) => a.id === act.activityId);
        if (actObj) {
          clearErrors(`activities.${actIdx}.unitId`);
          const resolvedUnitId = actObj.default_unit_id || '';
          if (act.unitId !== resolvedUnitId) {
            setValue(`activities.${actIdx}.unitId`, resolvedUnitId);
          }
        }
      }
    });
  }, [watchedActivities, activities, setValue, clearErrors, isEdit]);

  const { data: existingRecord, isLoading: loadingRecord } = useQuery({
    queryKey: ['actual-status', id],
    queryFn: async () => { const r = await api.get<any>(`/actual-status/${id}`); return r.data; },
    enabled: isEdit && Boolean(id),
  });

  useEffect(() => {
    if (existingRecord && isEdit && !hasHydratedEditRef.current) {
      const unitNames: Record<string, string> = {};
      const activitiesWithWorkers = (existingRecord.activities || []).map((a: any) => {
        if (a.unit_id && a.unit_name) unitNames[a.activity_id || a.activityId || ''] = a.unit_name;
        const actWorkers = (existingRecord.workers || [])
          .filter((w: any) => w.activity_id === a.id || !w.activity_id)
          .map((w: any) => ({
            workerCategoryId: w.worker_category_id,
            count: w.actual_count || 0,
            rate: w.rate || 0,
            rateBasis: normalizeRateBasis(w.rateBasis ?? w.rate_basis ?? 'HOURLY'),
            timeIn: w.time_in || '',
            timeOut: w.time_out || '',
          }));
        return {
          activityId: a.activity_id || a.activityId,
          qty: a.completed_quantity || a.qty || 0,
          unitId: a.unit_id || '',
          workerCategories: actWorkers.length > 0 ? actWorkers : [{ workerCategoryId: '', count: 0, rate: 0, rateBasis: 'DAILY', timeIn: '', timeOut: '' }],
        };
      });
      savedUnitNamesRef.current = unitNames;
      reset({
        projectId: existingRecord.project_id,
        date: existingRecord.work_date,
        blockId: existingRecord.block_id,
        requirementId: existingRecord.requirement_id || '',
        activities: activitiesWithWorkers,
        additionalLumpsumRequirements: (existingRecord.lumpsum || []).map((l: any) => ({
          categoryId: l.worker_category_id || l.categoryId,
          mandays: l.mandays || 0,
          rate: l.rate || 0,
          rateBasis: normalizeRateBasis(l.rateBasis ?? l.rate_basis ?? 'HOURLY'),
          timeIn: l.time_in || '',
          timeOut: l.time_out || '',
        })),
      });
      hasHydratedEditRef.current = true;
      setEditHydrated(true);
    }
  }, [existingRecord, isEdit, reset]);

  useEffect(() => {
    if (isEdit) {
      hasHydratedEditRef.current = false;
      setEditHydrated(false);
      savedUnitNamesRef.current = {};
    }
  }, [id, isEdit]);

  const isPendingRevision = isEdit && existingRecord?.status === 'PENDING_MANAGER_APPROVAL';

  const saveMutation = useMutation({
    mutationFn: async ({ data, shouldSubmit }: { data: FormData; shouldSubmit: boolean }) => {
      const eligibleEntries = linkedRequirement?.eligible_worker_entries;
      const allocatedEntryIds = !isEdit && Array.isArray(eligibleEntries)
        ? allocateEligibleWorkerEntryIds(data.activities, eligibleEntries)
        : [];
      const payload: Record<string, any> = {
        projectId: data.projectId,
        date: data.date,
        blockId: data.blockId,
        activities: data.activities.map((a, actIdx) => {
          const selectedAct = activities?.find((act: Activity) => act.id === a.activityId);
          return {
            activityId: a.activityId,
            qty: a.qty,
            unitId: selectedAct?.default_unit_id || undefined,
            workerCategories: (a.workerCategories || []).map((w, workerIdx) => ({
              workerCategoryId: w.workerCategoryId,
              count: w.count,
              rate: w.rate,
              rateBasis: normalizeRateBasis(w.rateBasis),
              timeIn: w.timeIn || undefined,
              timeOut: w.timeOut || undefined,
              workerEntryIds: allocatedEntryIds[actIdx]?.[workerIdx]?.length
                ? allocatedEntryIds[actIdx][workerIdx]
                : undefined,
            })),
          }
        }),
        additionalLumpsumRequirements: (data.additionalLumpsumRequirements || []).map(l => ({
          categoryId: l.categoryId,
          mandays: l.mandays,
          rate: l.rate || 0,
          rateBasis: normalizeRateBasis(l.rateBasis),
          timeIn: l.timeIn || undefined,
          timeOut: l.timeOut || undefined,
        })),
      };
      // New Actual Labour records must link an approved Requirement (legacy
      // records may be edited without one).
      if (!isEdit) payload.requirementId = data.requirementId;
      if (isPendingRevision) {
        payload.intent = 'REVISE';
        payload.expectedRevision = Number(existingRecord?.revision_number ?? existingRecord?.revisionNumber ?? 0);
      }

      let entityId = id;

      if (isEdit) {
        await api.put<any>(`/actual-status/${id}`, payload);
      } else {
        const res = await api.post<any>('/actual-status', payload);
        entityId = res.data?.id;
        createdIdRef.current = entityId ?? null;
      }

      const photoFiles = pendingPhotosRef.current || [];
      if (entityId && photoFiles.length > 0) {
        const formData = new FormData();
        for (const file of photoFiles) formData.append('photo', file);
        try {
          await api.upload(`/actual-status/${entityId}/photographs`, formData);
        } catch (err) {
          if (!isEdit) createdForPhotoRef.current = true;
          throw err;
        }
      }

      if (shouldSubmit && entityId && !isPendingRevision) {
        await api.post(`/actual-status/${entityId}/submit`);
      }

      return { shouldSubmit };
    },
    onSuccess: (res) => {
      createdForPhotoRef.current = false;
      createdIdRef.current = null;
      addToast(
        isPendingRevision
          ? 'Actual status revised and resubmitted for approval'
          : res.shouldSubmit ? 'Actual status submitted for approval' : (isEdit ? 'Actual status updated' : 'Actual status saved as draft'),
        'success'
      );
      queryClient.invalidateQueries({ queryKey: ['actual-status'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
      queryClient.invalidateQueries({ queryKey: ['report-project-summary'] });
      queryClient.invalidateQueries({ queryKey: ['report-category-detail'] });
      queryClient.invalidateQueries({ queryKey: ['report-budget-actual'] });
      queryClient.invalidateQueries({ queryKey: ['report-multi-summary'] });
      navigate('/actuals');
    },
    onError: (err: any) => {
      // The record was created but a photograph upload failed part-way: send
      // the engineer to the edit screen so they can retry the upload instead
      // of accidentally creating a duplicate record by pressing Save again.
      if (createdForPhotoRef.current && createdIdRef.current && !isEdit) {
        createdForPhotoRef.current = false;
        const entityId = createdIdRef.current;
        createdIdRef.current = null;
        addToast('Departmental Labour -Actual was created, but the photograph upload did not complete. Open the record to retry the upload.', 'error');
        navigate(`/actuals/${entityId}/edit`);
        return;
      }
      createdForPhotoRef.current = false;
      handleApiSubmitError(err, { addToast, setError: (name, error) => setError(name as any, error) });
    },
  });

  const onFormSubmit = (data: FormData) => {
    // New Actual Labour must be linked to an approved Requirement (legacy
    // records that predate the link may be edited without one). The selected id
    // must also be in the currently loaded approved list for the project, so a
    // stale/old requirement id can never be submitted.
    if (!isEdit) {
      const selectedReq = (approvedRequirements ?? []).find((r) => r.id === data.requirementId);
      if (!selectedReq) {
        setError('requirementId', { message: 'Select an approved Departmental Labour-Requirement.' });
        addToast('Select an approved Departmental Labour-Requirement.', 'error');
        return;
      }
      if (selectedReq.work_date) {
        const start = selectedReq.work_date;
        const end = selectedReq.work_end_date || start;
        if (data.date < start || data.date > end) {
          setError('requirementId', { message: "The actual date falls outside the linked Requirement's date range." });
          addToast("The actual date falls outside the linked Requirement's date range.", 'error');
          return;
        }
      }
    }

    // A worker category may only be used up to the number of that category's
    // entries that have received both PM and CH approval. This mirrors the
    // server-side guard so a stale browser selection cannot submit more
    // workers than the Requirement actually released.
    const eligibilityEntries = linkedRequirement?.eligible_worker_entries;
    if (Array.isArray(eligibilityEntries)) {
      if (eligibilityEntries.length === 0) {
        const message = 'No worker entries have received final CH approval yet.';
        setError('requirementId', { message });
        addToast(message, 'error');
        return;
      }
      const availableByCategory = new Map<string, { name: string; count: number }>();
      for (const entry of eligibilityEntries) {
        const categoryId = entry.worker_category_id;
        if (!categoryId) continue;
        const name = entry.worker_category_name || entry.category_name || 'Labour';
        const current = availableByCategory.get(categoryId) ?? { name, count: 0 };
        current.count += 1;
        availableByCategory.set(categoryId, current);
      }
      const requestedByCategory = new Map<string, number>();
      for (const activity of data.activities) {
        for (const worker of activity.workerCategories || []) {
          if (!worker.workerCategoryId) continue;
          requestedByCategory.set(
            worker.workerCategoryId,
            (requestedByCategory.get(worker.workerCategoryId) ?? 0) + (Number(worker.count) || 0),
          );
        }
      }
      for (const [categoryId, count] of requestedByCategory) {
        const available = availableByCategory.get(categoryId);
        if (!available) {
          const message = 'The selected worker entry does not belong to the linked Requirement or has not received final CH approval.';
          setError('requirementId', { message });
          addToast(message, 'error');
          return;
        }
        if (count > available.count) {
          const message = `Only ${available.count} fully approved ${available.name} worker ${available.count === 1 ? 'entry is' : 'entries are'} available for this Requirement.`;
          setError('requirementId', { message });
          addToast(message, 'error');
          return;
        }
      }
    }
    // Ignore a second submit while the first is still in flight so a
    // double-click cannot create a duplicate record or re-upload photographs.
    if (submittingRef.current) return;
    submittingRef.current = true;
    saveMutation.mutate(
      { data, shouldSubmit: shouldSubmitRef.current },
      { onSettled: () => { submittingRef.current = false; } }
    );
  };

  const onInvalid = (errs: FieldErrors<FormData>) => {
    focusFirstInvalidField(errs);
    addToast(REQUIRED_FIELDS_MESSAGE, 'error');
  };

  useEffect(() => {
    const container = document.querySelector<HTMLElement>('.app-content-shell');
    if (container) {
      container.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, []);

  const activitiesLoaded = Array.isArray(activities);
  const isPending = saveMutation.isPending;

  const computeActivityTotal = (actIdx: number): number => {
    const act = watchedActivities?.[actIdx];
    if (!act?.workerCategories) return 0;
    return act.workerCategories.reduce((sum, w) => {
      const minutes = workedMinutes(w.timeIn, w.timeOut);
      if (minutes === null) return sum;
      return sum + (workerTotal(w.count || 0, minutes, w.rate || 0, normalizeRateBasis(w.rateBasis), standardHours) ?? 0);
    }, 0);
  };

  const computeGrandTotal = (): number => {
    const totalWorkers = watchedActivities
      ? watchedActivities.reduce((sum, _, idx) => sum + computeActivityTotal(idx), 0)
      : 0;
    const totalLumpsum = (watch('additionalLumpsumRequirements') || []).reduce(
      (sum, ls) => {
        if (!ls) return sum;
        const minutes = workedMinutes(ls.timeIn, ls.timeOut);
        if (minutes === null) return sum;
        return sum + (workerTotal(ls.mandays || 0, minutes, ls.rate || 0, normalizeRateBasis(ls.rateBasis), standardHours) ?? 0);
      }, 0
    );
    return totalWorkers + totalLumpsum;
  };

  const activitiesArrayError = Array.isArray(errors.activities) ? undefined : errors.activities?.message;

  if (isEdit && (loadingRecord || (!editHydrated && Boolean(existingRecord)) || (editHydrated && (blocksIsLoading || activitiesIsLoading)))) return <PageLoading />;

  return (
    <div className="actual-labour-page">
      <div className="mb-6">
        <h1 className="text-2xl font-heading font-bold text-primary">{isEdit ? 'Edit Departmental Labour -Actual' : 'New Departmental Labour -Actual'}</h1>
        <p className="text-sm text-secondary mt-1">Enter activities with worker categories, time tracking and approvals.</p>
      </div>

      <form onSubmit={handleSubmit(onFormSubmit, onInvalid)} className="actual-labour-form space-y-6">
        <Card>
          <CardHeader><CardTitle className="text-base">Project Details</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Project *</Label>
              <Select onValueChange={v => setValue('projectId', v)} value={watchedProjectId || ''}>
                <SelectTrigger data-field-name="projectId" aria-invalid={errors.projectId ? true : undefined} aria-describedby={errors.projectId ? 'projectId-error' : undefined}><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent>
                  {projects?.map((p: Project) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <FormError id="projectId-error" message={errors.projectId?.message} />
            </div>
            <div>
              <Label>Departmental Labour-Requirement *</Label>
              {isEdit ? (
                <EditRequirementDisplay existingRecord={existingRecord} requirements={approvedRequirements} />
              ) : requirementsLoading ? (
                <div className="h-10 flex items-center px-3 rounded-md border bg-muted/40 text-sm text-secondary">Loading approved Departmental Labour-Requirement...</div>
              ) : requirementsError ? (
                <div className="space-y-2">
                  <p className="text-sm text-error">Unable to load approved Departmental Labour-Requirement.</p>
                  <Button type="button" variant="outline" size="sm" onClick={() => refetchRequirements()}>Retry</Button>
                </div>
              ) : (
                <>
                  <SearchableSelect
                    value={watchedRequirementId || ''}
                    onValueChange={v => setValue('requirementId', v)}
                    options={requirementOptions}
                    placeholder="Select approved Departmental Labour-Requirement"
                    disabled={!watchedProjectId || requirementOptions.length === 0}
                    searchPlaceholder="Search by project, description, block, ID, dates, activity, or category"
                    emptyMessage="No CH-final-approved worker entries are available for Actual creation."
                    data-field-name="requirementId"
                    aria-invalid={errors.requirementId ? true : undefined}
                    aria-describedby={errors.requirementId ? 'requirementId-error' : undefined}
                  />
                  {watchedProjectId && requirementOptions.length === 0 && (
                    <p className="text-xs text-secondary mt-1">No CH-final-approved worker entries are available for Actual creation.</p>
                  )}
                </>
              )}
              <FormError id="requirementId-error" message={errors.requirementId?.message} />
              {eligibleWorkerEntries && (
                eligibleWorkerEntries.length === 0 ? (
                  <p className="text-xs text-error mt-2">No worker entries have received final CH approval yet.</p>
                ) : (
                  <div className="mt-2 rounded-md border bg-muted/30 px-3 py-2 text-xs text-secondary">
                    <span className="font-medium text-primary">
                      {eligibleWorkerEntries.length} CH-approved worker {eligibleWorkerEntries.length === 1 ? 'entry' : 'entries'} available:
                    </span>{' '}
                    {[...eligibleCategoryCounts!.entries()].map(([categoryId, category]) => `${category.name} (${category.count})`).join(', ')}
                  </div>
                )
              )}
            </div>
            <div>
              <Label>Date *</Label>
              <DatePicker {...register('date')} aria-invalid={errors.date ? true : undefined} aria-describedby={errors.date ? 'date-error' : undefined} className={cn(errors.date && 'border-error')} />
              <FormError id="date-error" message={errors.date?.message} />
            </div>
            <div>
              <Label>Block *</Label>
              <Select onValueChange={v => setValue('blockId', v)} value={watchedBlockId || ''}>
                <SelectTrigger data-field-name="blockId" aria-invalid={errors.blockId ? true : undefined} aria-describedby={errors.blockId ? 'blockId-error' : undefined}><SelectValue placeholder="Select block" /></SelectTrigger>
                <SelectContent>
                  {blocks?.map((b: Block) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <FormError id="blockId-error" message={errors.blockId?.message} />
            </div>
          </CardContent>
        </Card>

        {activityFields.map((actField, actIdx) => {
          const act = watchedActivities?.[actIdx];
          const activityObj = activities?.find((a: Activity) => a.id === act?.activityId);
          const actErrors: any = Array.isArray(errors.activities) ? errors.activities?.[actIdx] : undefined;
          return (
            <Card key={actField.id}>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base min-w-0">Activity {actIdx + 1}</CardTitle>
                {activityFields.length > 1 && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeActivity(actIdx)}>
                    <Trash2 size={16} className="text-red-500 mr-1" /> Remove
                    
                  </Button>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div className="min-w-0">
                    <Label>Activity *</Label>
                    {activitiesError ? (
                      <div className="space-y-2">
                        <p className="text-sm text-error">Unable to load activities.</p>
                        <Button type="button" variant="outline" size="sm" onClick={() => refetchActivities()}>Retry</Button>
                      </div>
                    ) : (
                      <Select onValueChange={v => setValue(`activities.${actIdx}.activityId`, v)} value={act?.activityId || ''}>
                        <SelectTrigger data-field-name={`activities.${actIdx}.activityId`} aria-invalid={actErrors?.activityId ? true : undefined} aria-describedby={actErrors?.activityId ? `activities-${actIdx}-activityId-error` : undefined}><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          {activities?.map((a: Activity) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                    <FormError id={`activities-${actIdx}-activityId-error`} message={actErrors?.activityId?.message} />
                  </div>
                  <div className="min-w-0">
                    <Label>Qty *</Label>
                    <Input type="number" min={0} {...register(`activities.${actIdx}.qty`)} aria-invalid={actErrors?.qty ? true : undefined} aria-describedby={actErrors?.qty ? `activities-${actIdx}-qty-error` : undefined} className={cn(actErrors?.qty && 'border-error')} />
                    <FormError id={`activities-${actIdx}-qty-error`} message={actErrors?.qty?.message} />
                  </div>
                  <div className="min-w-0">
                    <Label>Unit</Label>
                    <Input value={activityObj?.default_unit_name || savedUnitNamesRef.current[act?.activityId || ''] || ''} readOnly placeholder="Auto" />
                    {activitiesLoaded && Boolean(act?.activityId) && Boolean(activityObj) && !activityObj?.default_unit_id && (
                      <p className="text-xs text-red-500 mt-1">The selected activity does not have a default unit. Configure it in Activity Master.</p>
                    )}
                  </div>
                  <div className="flex items-end">
                    <div className="text-sm">
                      <span className="text-secondary">Activity Total: </span>
                      <span className="font-semibold">&#8377;{computeActivityTotal(actIdx).toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                </div>

                <WorkerCategoryRows
                  actIdx={actIdx}
                  control={control}
                  register={register}
                  watch={watch}
                  setValue={setValue}
                  workerCategoryOptions={workerCategoryOptions}
                  restrictedToEligible={Boolean(eligibleWorkerEntries)}
                  errors={errors}
                  standardHours={standardHours}
                />
              </CardContent>
            </Card>
          );
        })}

        <div className="flex flex-col items-center gap-2">
          <Button type="button" variant="outline" onClick={() => appendActivity({ activityId: '', qty: 0, unitId: '', workerCategories: [{ workerCategoryId: '', count: 0, rate: 0, rateBasis: 'DAILY', timeIn: '', timeOut: '' }] })}>
            <Plus size={14} /> Add Activity
          </Button>
          <FormError message={activitiesArrayError} />
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Additional Lump-sum Requirement (Mandays)</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={() => {
              const current = watch('additionalLumpsumRequirements') || [];
              setValue('additionalLumpsumRequirements', [...current, { categoryId: '', mandays: 0, rate: 0, rateBasis: 'DAILY', timeIn: '', timeOut: '' }]);
            }}>
              <Plus size={14} /> Add Category
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {(watch('additionalLumpsumRequirements') || []).map((_, idx) => {
              const lsErrors: any = Array.isArray(errors.additionalLumpsumRequirements) ? errors.additionalLumpsumRequirements?.[idx] : undefined;
              const lsMandays = watch(`additionalLumpsumRequirements.${idx}.mandays`) || 0;
              const lsRate = watch(`additionalLumpsumRequirements.${idx}.rate`) || 0;
              const lsTimeIn = watch(`additionalLumpsumRequirements.${idx}.timeIn`) || '';
              const lsTimeOut = watch(`additionalLumpsumRequirements.${idx}.timeOut`) || '';
              const lsMinutes = workedMinutes(lsTimeIn, lsTimeOut);
              const lsRateBasis = normalizeRateBasis(watch(`additionalLumpsumRequirements.${idx}.rateBasis`));
              const lsTotal = lsMinutes === null ? null : workerTotal(lsMandays, lsMinutes, lsRate, lsRateBasis, standardHours);
              return (
                <div key={idx} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end border rounded-lg p-3 min-w-0">
                  <div className="md:col-span-2 min-w-0">
                    <Label>Worker Category *</Label>
                    <Select onValueChange={v => setValue(`additionalLumpsumRequirements.${idx}.categoryId`, v)} value={watch(`additionalLumpsumRequirements.${idx}.categoryId`) || ''}>
                      <SelectTrigger data-field-name={`additionalLumpsumRequirements.${idx}.categoryId`} aria-invalid={lsErrors?.categoryId ? true : undefined} aria-describedby={lsErrors?.categoryId ? `lumpsum-${idx}-categoryId-error` : undefined}><SelectValue placeholder="Select category" /></SelectTrigger>
                      <SelectContent>
                        {labourCategories?.map((c: LabourCategory) => <SelectItem key={c.id} value={c.id}>{c.name} — {(c.skill_level || c.skillLevel || 'UNCLASSIFIED').replace('_', ' ')}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormError id={`lumpsum-${idx}-categoryId-error`} message={lsErrors?.categoryId?.message} />
                  </div>
                  <div className="md:col-span-1 min-w-0">
                    <Label>Mandays *</Label>
                    <Input type="number" min={0} step="0.5" {...register(`additionalLumpsumRequirements.${idx}.mandays`)} aria-invalid={lsErrors?.mandays ? true : undefined} aria-describedby={lsErrors?.mandays ? `lumpsum-${idx}-mandays-error` : undefined} className={cn(lsErrors?.mandays && 'border-error')} />
                    <FormError id={`lumpsum-${idx}-mandays-error`} message={lsErrors?.mandays?.message} />
                  </div>
                  <div className="md:col-span-1 min-w-0">
                    <Label>{lsRateBasis === 'HOURLY' ? 'Hourly Rate (₹/hr)' : 'Daily Rate (₹/day)'}</Label>
                    <Input type="number" min={0} step="0.01" {...register(`additionalLumpsumRequirements.${idx}.rate`)} aria-invalid={lsErrors?.rate ? true : undefined} aria-describedby={lsErrors?.rate ? `lumpsum-${idx}-rate-error` : undefined} className={cn(lsErrors?.rate && 'border-error')} />
                    <FormError id={`lumpsum-${idx}-rate-error`} message={lsErrors?.rate?.message} />
                  </div>
                  <div className="md:col-span-1 min-w-0">
                    <Label>Time In *</Label>
                    <Input type="time" {...register(`additionalLumpsumRequirements.${idx}.timeIn`)} aria-invalid={lsErrors?.timeIn ? true : undefined} aria-describedby={lsErrors?.timeIn ? `lumpsum-${idx}-timeIn-error` : undefined} className={cn(lsErrors?.timeIn && 'border-error')} />
                    <FormError id={`lumpsum-${idx}-timeIn-error`} message={lsErrors?.timeIn?.message} />
                  </div>
                  <div className="md:col-span-1 min-w-0">
                    <Label>Time Out *</Label>
                    <Input type="time" {...register(`additionalLumpsumRequirements.${idx}.timeOut`)} aria-invalid={lsErrors?.timeOut ? true : undefined} aria-describedby={lsErrors?.timeOut ? `lumpsum-${idx}-timeOut-error` : undefined} className={cn(lsErrors?.timeOut && 'border-error')} />
                    <FormError id={`lumpsum-${idx}-timeOut-error`} message={lsErrors?.timeOut?.message} />
                  </div>
                  <div className="md:col-span-1 min-w-0">
                    <Label>Worked Hours</Label>
                    <div className="h-10 flex items-center text-sm font-medium">{lsMinutes === null ? '—' : formatWorkedHours(lsMinutes)}</div>
                  </div>
                  <div className="md:col-span-1 min-w-0">
                    <Label>Total</Label>
                    <div className="h-10 flex items-center text-sm font-medium">{lsTotal === null ? '—' : `₹${lsTotal.toLocaleString('en-IN')}`}</div>
                  </div>
                  <div className="md:col-span-4 flex justify-end">
                    <Button type="button" variant="ghost" size="icon" onClick={() => {
                      const current = watch('additionalLumpsumRequirements') || [];
                      setValue('additionalLumpsumRequirements', current.filter((_, i) => i !== idx));
                    }}>
                      <Trash2 size={16} className="text-red-500" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2"><Camera size={18} /> Photographs</CardTitle>
          </CardHeader>
          <CardContent>
            <PhotographSection
              entityId={isEdit ? id : undefined}
              savedPhotos={isEdit ? (existingRecord?.attachments || []) : []}
              canModify
              onPendingChange={(files) => { pendingPhotosRef.current = files; }}
            />
          </CardContent>
        </Card>

        <Card className="grand-total-card">
          <CardContent className="py-4">
            <div className="flex justify-end text-lg font-semibold">
              Grand Total: &#8377;{computeGrandTotal().toLocaleString('en-IN')}
            </div>
          </CardContent>
        </Card>

        <div className="actual-form-actions">
          <button type="button" className="btn-action btn-action--secondary" onClick={() => navigate('/actuals')}>Cancel</button>
          {!isPendingRevision && (
            <button type="submit" className="btn-action btn-action--outline" onClick={() => { shouldSubmitRef.current = false; }} disabled={isPending}>
              {isPending ? null : <Save size={16} />} Save Draft
            </button>
          )}
          <button type="submit" className="btn-action btn-action--primary" onClick={() => { shouldSubmitRef.current = true; }} disabled={isPending}>
            {isPending ? null : <Send size={16} />} {isPendingRevision ? 'Save & Resubmit' : 'Send for Approval'}
          </button>
        </div>
      </form>
    </div>
  );
}

function EditRequirementDisplay({
  existingRecord,
  requirements,
}: {
  existingRecord: any;
  requirements?: ApprovedRequirementOption[];
}) {
  const requirementId = existingRecord?.requirement_id;
  const linked = requirements?.find((r: ApprovedRequirementOption) => r.id === requirementId);
  const fallback = existingRecord?.requirement_number || 'Not linked to a Departmental Labour-Requirement';
  return (
    <div
      className="flex h-10 items-center justify-between gap-2 rounded-lg border bg-muted/40 px-3 text-sm text-primary"
      title={linked ? requirementSecondaryLabel(linked) : undefined}
    >
      <span className="truncate">{linked ? requirementPrimaryLabel(linked) : fallback}</span>
      {linked && <span className="shrink-0 text-xs text-secondary">{linked.requirement_number}</span>}
    </div>
  );
}

function WorkerCategoryRows({
  actIdx, control, register, watch, setValue, workerCategoryOptions, restrictedToEligible, errors, standardHours,
}: {
  actIdx: number;
  control: any;
  register: any;
  watch: any;
  setValue: any;
  workerCategoryOptions?: WorkerCategoryOption[];
  restrictedToEligible?: boolean;
  errors: any;
  standardHours: number;
}) {
  const { fields, append, remove } = useFieldArray({ control, name: `activities.${actIdx}.workerCategories` });
  const watchedWorkers = watch(`activities.${actIdx}.workerCategories`);

  return (
    <div className="border-t pt-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-medium text-secondary">Worker Categories</p>
        <Button type="button" variant="outline" size="sm" onClick={() => append({ workerCategoryId: '', count: 0, rate: 0, rateBasis: 'DAILY', timeIn: '', timeOut: '' })}>
          <Plus size={14} /> Add Worker
        </Button>
      </div>
      {restrictedToEligible && (!workerCategoryOptions || workerCategoryOptions.length === 0) && (
        <p className="text-xs text-error mb-2">No worker entries have received final CH approval yet.</p>
      )}
      <div>
      {fields.map((field, wIdx) => {
        const w = watchedWorkers?.[wIdx];
        const wMinutes = workedMinutes(w?.timeIn, w?.timeOut);
        const wRateBasis = normalizeRateBasis(w?.rateBasis);
        const wTotal = wMinutes === null ? null : workerTotal(w?.count || 0, wMinutes, w?.rate || 0, wRateBasis, standardHours);
        const wcErrors: any = errors?.activities?.[actIdx]?.workerCategories?.[wIdx];
        return (
          <div key={field.id} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 items-end mb-3 border rounded-lg p-3 min-w-0">
            <div className="sm:col-span-2 min-w-0">
              <Label>Category *</Label>
              <Select onValueChange={v => setValue(`activities.${actIdx}.workerCategories.${wIdx}.workerCategoryId`, v)} value={w?.workerCategoryId || ''}>
                <SelectTrigger data-field-name={`activities.${actIdx}.workerCategories.${wIdx}.workerCategoryId`} aria-invalid={wcErrors?.workerCategoryId ? true : undefined} aria-describedby={wcErrors?.workerCategoryId ? `wc-${actIdx}-${wIdx}-categoryId-error` : undefined}><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {workerCategoryOptions?.map((c: WorkerCategoryOption) => <SelectItem key={c.id} value={c.id}>{c.name} — {(c.skill_level || c.skillLevel || 'UNCLASSIFIED').replace('_', ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
              <FormError id={`wc-${actIdx}-${wIdx}-categoryId-error`} message={wcErrors?.workerCategoryId?.message} />
            </div>
            <div className="min-w-0">
              <Label>Count *</Label>
              <Input type="number" min={1} {...register(`activities.${actIdx}.workerCategories.${wIdx}.count`)} aria-invalid={wcErrors?.count ? true : undefined} aria-describedby={wcErrors?.count ? `wc-${actIdx}-${wIdx}-count-error` : undefined} className={cn(wcErrors?.count && 'border-error')} />
              <FormError id={`wc-${actIdx}-${wIdx}-count-error`} message={wcErrors?.count?.message} />
            </div>
            <div className="min-w-0">
              <Label>{wRateBasis === 'HOURLY' ? 'Hourly Rate (₹/hr)' : 'Daily Rate (₹/day)'}</Label>
              <Input type="number" min={0} step="0.01" {...register(`activities.${actIdx}.workerCategories.${wIdx}.rate`)} aria-invalid={wcErrors?.rate ? true : undefined} aria-describedby={wcErrors?.rate ? `wc-${actIdx}-${wIdx}-rate-error` : undefined} className={cn(wcErrors?.rate && 'border-error')} />
              <FormError id={`wc-${actIdx}-${wIdx}-rate-error`} message={wcErrors?.rate?.message} />
            </div>
            <div className="min-w-0">
              <Label>Worked Hours</Label>
              <div className="h-10 flex items-center text-sm font-medium">{wMinutes === null ? '—' : formatWorkedHours(wMinutes)}</div>
            </div>
            <div className="min-w-0">
              <Label>Total</Label>
              <div className="h-10 flex items-center text-sm font-medium">{wTotal === null ? '—' : `₹${wTotal.toLocaleString('en-IN')}`}</div>
            </div>
            <div className="min-w-0">
              <Label>Time In *</Label>
              <Input type="time" {...register(`activities.${actIdx}.workerCategories.${wIdx}.timeIn`)} aria-invalid={wcErrors?.timeIn ? true : undefined} aria-describedby={wcErrors?.timeIn ? `wc-${actIdx}-${wIdx}-timeIn-error` : undefined} className={cn(wcErrors?.timeIn && 'border-error')} />
              <FormError id={`wc-${actIdx}-${wIdx}-timeIn-error`} message={wcErrors?.timeIn?.message} />
            </div>
            <div className="min-w-0">
              <Label>Time Out *</Label>
              <Input type="time" {...register(`activities.${actIdx}.workerCategories.${wIdx}.timeOut`)} aria-invalid={wcErrors?.timeOut ? true : undefined} aria-describedby={wcErrors?.timeOut ? `wc-${actIdx}-${wIdx}-timeOut-error` : undefined} className={cn(wcErrors?.timeOut && 'border-error')} />
              <FormError id={`wc-${actIdx}-${wIdx}-timeOut-error`} message={wcErrors?.timeOut?.message} />
            </div>
            <div className="sm:col-span-2 md:col-span-4 flex justify-end">
              {fields.length > 1 && (
                <Button type="button" variant="ghost" size="icon" onClick={() => remove(wIdx)}>
                  <Trash2 size={16} className="text-red-500" />
                </Button>
              )}
            </div>
          </div>
        );
      })}
      </div>
    </div>
  );
}

